import { useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Download } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * ShareCardButton — one-tap "Share my newspaper" button that fetches a
 * server-generated 1080×1350 PNG and hands it to the OS native share
 * sheet (Instagram Stories, WhatsApp, Messages, X, Threads…). On
 * desktop/older browsers it falls back to a PNG download.
 *
 * Props:
 *   snapshotId (string, required)  — the daily_snapshots id to render
 *   dateLabel  (string, optional)  — used in the share title, e.g. "July 13, 1985"
 *   className  (string, optional)  — custom classes to override the pill style
 */
export default function ShareCardButton({ snapshotId, dateLabel = "", className = "" }) {
  const [busy, setBusy] = useState(false);

  const canShareFiles = () => {
    if (typeof navigator === "undefined") return false;
    if (typeof navigator.canShare !== "function") return false;
    try {
      // Probe with a tiny dummy PNG to see if files are allowed
      return navigator.canShare({ files: [new File([""], "probe.png", { type: "image/png" })] });
    } catch { return false; }
  };

  const handleShare = async () => {
    if (busy || !snapshotId) return;
    setBusy(true);
    trackEvent("share_card_click", { snapshot_id: snapshotId });
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/share/card/${snapshotId}.png`, { cache: "force-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = `timecapsule24-${snapshotId}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      const shareTitle = dateLabel ? `My day in history — ${dateLabel}` : "My day in history · TimeCapsule 24";
      const shareText = "See your own day at timecapsule24.tv";

      if (canShareFiles()) {
        await navigator.share({
          files: [file],
          title: shareTitle,
          text: shareText,
        });
        trackEvent("share_card_shared", { snapshot_id: snapshotId, method: "native" });
      } else {
        // Fallback: trigger a download so user can share manually
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        toast.success("Card downloaded — post it to your favourite app!");
        trackEvent("share_card_shared", { snapshot_id: snapshotId, method: "download" });
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user cancelled — silent
      console.error(err);
      toast.error("Couldn't build the share card. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const nativeAvailable = canShareFiles();
  const Icon = busy ? Loader2 : (nativeAvailable ? ImageIcon : Download);

  return (
    <button
      onClick={handleShare}
      disabled={busy || !snapshotId}
      className={
        className ||
        "inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-4 py-2.5 font-mono uppercase tracking-widest text-[11px] hover:bg-ink hover:border-ink disabled:opacity-60 disabled:cursor-wait"
      }
      data-testid="share-card-btn"
      aria-label="Share my newspaper as an image"
    >
      <Icon size={14} className={busy ? "animate-spin" : ""} />
      {busy ? "Building card…" : (nativeAvailable ? "Share my newspaper" : "Download card")}
    </button>
  );
}
